<template>
  <div class="unpaid-bills-container">
    <div class="header">
      <div class="title">待支付账单</div>
      <div class="stats-bar">
        <div class="stat-item">
          <div class="stat-value">{{ totalAmount }}元</div>
          <div class="stat-label">待支付总额</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ total }}</div>
          <div class="stat-label">待付账单数</div>
        </div>
      </div>
      <div class="search-box">
        <van-search
          v-model="searchValue"
          placeholder="输入收件人手机号进行查询"
          shape="round"
          @search="onSearch"
        />
      </div>
    </div>

    <div class="bills-list" v-if="bills.length > 0">
      <div 
        v-for="(bill, index) in bills" 
        :key="bill.id || index"
        class="bill-item"
        @click="goToBillDetail(bill.id)"
      >
        <div class="bill-header">
          <div class="order-info">
            <div class="order-type">
              <van-tag :type="getDictTagType(DELIVERY_TYPE, bill.deliveryType)">
                {{ getDictLabel(DELIVERY_TYPE, bill.deliveryType) }}
              </van-tag>
            </div>
            <div class="order-no">订单号：{{ bill.code }}</div>
          </div>
          <div class="bill-status">
            <van-tag :type="getDictTagType(BILL_STATUS, 'TO_BE_PAY')" plain round>
              {{ getDictLabel(BILL_STATUS, 'TO_BE_PAY') }}
            </van-tag>
          </div>
        </div>

        <div class="bill-info">
          <div class="info-row">
            <div class="info-label">物品名称：</div>
            <div class="info-value">
              <van-tag :type="getDictTagType(GOODS_NAME, bill.goodsName)" plain round>
                {{ getDictLabel(GOODS_NAME, bill.goodsName) || bill.goodsName }}
              </van-tag>
            </div>
          </div>
          <div class="info-row">
            <div class="info-label">收件人：</div>
            <div class="info-value">{{ bill.receiverName }}</div>
          </div>
          <div class="info-row">
            <div class="info-label">目的地：</div>
            <div class="info-value">{{ bill.receiverAddress }}</div>
          </div>
          <div class="info-row" v-if="bill.sendDate">
            <div class="info-label">发货日期：</div>
            <div class="info-value">{{ bill.sendDate }}</div>
          </div>
          <div class="info-row" v-if="bill.arrivalDate">
            <div class="info-label">到货日期：</div>
            <div class="info-value">{{ bill.arrivalDate }}</div>
          </div>
          <div class="info-row">
            <div class="info-label">订单状态：</div>
            <div class="info-value">
              <van-tag :type="getDictTagType(BILL_STATUS, bill.status)" plain>
                {{ getDictLabel(BILL_STATUS, bill.status) }}
              </van-tag>
            </div>
          </div>
        </div>

        <div class="bill-footer">
          <div class="bill-amount">
            <span class="amount-label">应收款：</span>
            <span class="amount-value">¥{{ bill.totalFee }}</span>
          </div>
          <div class="bill-actions">
            <div class="action-btn pay-btn" @click.stop="payBill(bill.id)">立即支付</div>
          </div>
        </div>
      </div>
    </div>

    <div class="empty-state" v-else>
      <van-empty description="暂无待支付账单" />
    </div>

    <div class="load-more" v-if="hasMore && bills.length > 0">
      <van-loading v-if="loading" type="spinner" size="24px">加载中...</van-loading>
      <div v-else @click="loadMore" class="load-more-text">加载更多</div>
    </div>
  </div>
</template>

<script>
import { Search, Tag, Empty, Loading, Toast } from 'vant'
import { getBillList } from '@/api/bill'
import { GOODS_NAME, BILL_STATUS, DELIVERY_TYPE, getDictLabel, getDictTagType } from '@/utils/dict'

export default {
  name: 'UnpaidBills',
  components: {
    [Search.name]: Search,
    [Tag.name]: Tag,
    [Empty.name]: Empty,
    [Loading.name]: Loading
  },
  data() {
    return {
      searchValue: '',
      loading: false,
      hasMore: true,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      bills: [],
      GOODS_NAME,
      BILL_STATUS,
      DELIVERY_TYPE
    }
  },
  computed: {
    totalAmount() {
      return this.bills.reduce((total, bill) => {
        const fee = parseFloat(bill.totalFee || 0)
        return total + (isNaN(fee) ? 0 : fee)
      }, 0).toFixed(2)
    }
  },
  created() {
    this.fetchBills()
  },
  activated() {
    // keep-alive 缓存的组件在激活时重新获取数据
    // 清空搜索值，确保每次进入页面都是全新的数据
    this.searchValue = ''
    this.pageNum = 1
    
    // 使用 nextTick 确保页面先渲染完成，再请求数据，避免阻塞页面切换
    this.$nextTick(() => {
      this.fetchBills()
    })
  },
  methods: {
    getDictLabel,
    getDictTagType,
    async fetchBills(isLoadMore = false) {
      try {
        this.loading = true
        
        const params = {
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          status: 'TO_BE_PAY', // 待支付账单状态
          phone: this.searchValue || undefined
        }
        
        const res = await getBillList(params)
        
        if (res.code === 200) {
          const { list, total } = res.data
          console.log(list)
          console.log(total)
          
          if (isLoadMore) {
            this.bills = [...this.bills, ...list]
          } else {
            this.bills = list
          }
          
          this.total = total
          this.hasMore = this.bills.length < total
        } else {
          Toast(res.msg || '获取账单列表失败')
        }
      } catch (error) {
        console.error('获取账单列表出错', error)
        Toast('获取账单列表失败')
      } finally {
        this.loading = false
      }
    },
    
    onSearch() {
      this.pageNum = 1
      this.fetchBills()
    },
    
    goToBillDetail(id) {
      this.$router.push(`/billDetail/${id}`)
    },
    
    payBill(id) {
      // 跳转到支付页面
      this.$router.push({
        path: '/billDetail/' + id,
        query: { action: 'pay' }
      })
    },
    
    loadMore() {
      if (this.loading || !this.hasMore) return
      
      this.pageNum++
      this.fetchBills(true)
    }
  }
}
</script>

<style scoped>
.unpaid-bills-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-bottom: 50px;
}

.header {
  background-color: #ffffff;
  padding: 10px 0;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.title {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin: 10px 0;
  color: #333;
}

.stats-bar {
  display: flex;
  padding: 10px 15px;
  background-color: #eef7ff;
  margin: 0 15px 10px;
  border-radius: 8px;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: #1989fa;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.search-box {
  padding: 0 15px;
}

.bills-list {
  padding: 10px 15px;
}

.bill-item {
  background-color: #ffffff;
  border-radius: 12px;
  margin-bottom: 15px;
  padding: 15px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s;
}

.bill-item:active {
  transform: scale(0.98);
}

.bill-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.order-info {
  display: flex;
  flex-direction: column;
}

.order-type {
  margin-bottom: 5px;
}

.order-no {
  font-size: 12px;
  color: #999;
}

.bill-status {
  font-size: 14px;
  font-weight: bold;
}

.bill-info {
  margin-bottom: 15px;
  background-color: #fafafa;
  padding: 10px;
  border-radius: 8px;
}

.info-row {
  display: flex;
  margin: 5px 0;
  font-size: 14px;
}

.info-label {
  min-width: 80px;
  color: #666;
  font-weight: 500;
}

.info-value {
  flex: 1;
  color: #333;
}

.bill-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.bill-amount {
  padding: 5px 0;
}

.amount-label {
  font-size: 14px;
  color: #666;
}

.amount-value {
  font-size: 18px;
  font-weight: bold;
  color: #f44;
  margin-left: 5px;
}

.bill-actions {
  display: flex;
}

.action-btn {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  margin-left: 10px;
  font-weight: 500;
  cursor: pointer;
}

.cancel-btn {
  border: 1px solid #ddd;
  color: #666;
}

.pay-btn {
  background-color: #1989fa;
  color: #fff;
  box-shadow: 0 2px 6px rgba(25, 137, 250, 0.3);
}

.empty-state {
  padding: 40px 0;
}

.load-more {
  text-align: center;
  padding: 15px 0;
  color: #999;
}

.load-more-text {
  font-size: 14px;
}
</style> 